import React, { useCallback, useEffect, useState } from 'react';
import { autoId, formFieldErrorPropsV2 } from '@workday/upc-common';
import styles from './account-setup-form.module.scss';
import { Trans, useTranslation } from 'react-i18next';
import {
    useEnrollmentAuthenticatorRegisterMutation,
    useEnrollmentPasswordSubmitMutation
} from '../../../slices/auth-api.slice';
import { useSelector } from 'react-redux';
import {
    selectUserEmail,
    selectUserFirstName,
    selectUserId,
    selectUserLastName
} from '../../../slices/registration.slice';
import { PrimaryButton } from '@workday/canvas-kit-react/button';
import { ID_FIRST_NAME_FIELD, ID_LAST_NAME_FIELD, ID_PASSWORD_BUTTON } from '../../../autoIds';
import { TextInput } from '@workday/canvas-kit-react/text-input';
import { FormField } from '@workday/canvas-kit-react/form-field';
import { useForm } from 'react-hook-form';
import { PasswordTextFieldWithConfirm } from '../../../shared/passwordTextFieldWithConfirm/PasswordTextFieldWithConfirm';
import { TranslatedExternalLink } from '../../../shared/translated-external-link';

interface FirstNameLastNameState {
    firstName: string;
    lastName: string;
}
export function AccountSetupForm({ steps }) {
    const { t } = useTranslation('auth');

    const userEmail = useSelector(selectUserEmail);
    const userId = useSelector(selectUserId);
    const userFirstName = useSelector(selectUserFirstName);
    const userLastName = useSelector(selectUserLastName);

    const [enrollmentPasswordSubmit, enrollmentPasswordSubmitMutation] = useEnrollmentPasswordSubmitMutation();

    const [enrollmentAuthenticatorRegister, enrollmentAuthenticatorRegisterMutation] =
        useEnrollmentAuthenticatorRegisterMutation();

    useEffect(() => {
        if (enrollmentAuthenticatorRegisterMutation.isSuccess) {
            steps.next();
        }
    }, [steps, enrollmentAuthenticatorRegisterMutation]);

    useEffect(() => {
        if (enrollmentPasswordSubmitMutation.isSuccess) {
            enrollmentAuthenticatorRegister();
        }
    }, [enrollmentAuthenticatorRegister, enrollmentPasswordSubmitMutation.isSuccess]);

    const isLoading = enrollmentPasswordSubmitMutation.isLoading || enrollmentPasswordSubmitMutation.isLoading;

    const [showPasswordErrors, setShowPasswordErrors] = useState(false);
    const [isPasswordValid, setIsValidPassword] = useState(false);
    const [password, setPassword] = useState('');

    const { register, handleSubmit, formState } = useForm<FirstNameLastNameState>({
        defaultValues: {
            firstName: userFirstName || '',
            lastName: userLastName || ''
        },
        reValidateMode: 'onChange'
    });

    function onSubmit({ firstName, lastName }) {
        if (isPasswordValid) {
            enrollmentPasswordSubmit({ password, userId, userFirstName: firstName, userLastName: lastName });
        } else {
            setShowPasswordErrors(true);
        }
    }
    // We need to manually show the password errors (if any) here
    function onInvalidFormSubmission() {
        setShowPasswordErrors(!isPasswordValid);
    }

    const passwordUpdated = useCallback((isValid, password) => {
        setIsValidPassword(isValid);
        setPassword(password);
    }, []);

    return (
        <>
            <h1 className={styles.title}>{t('AccountSetupForm.setupYour')}</h1>
            <address className={styles.email}>{userEmail}</address>
            <div className={styles.container}>
                <form onSubmit={handleSubmit(onSubmit, onInvalidFormSubmission)}>
                    <div className={styles.formFields}>
                        <FormField
                            grow={true}
                            inputId={ID_FIRST_NAME_FIELD}
                            label={t('AccountSetupForm.firstNameLabel')}
                            {...formFieldErrorPropsV2(
                                formState.errors.firstName,
                                undefined,
                                t('Required.fieldErrorLabel', { ns: 'shared' })
                            )}>
                            <TextInput
                                {...autoId(ID_FIRST_NAME_FIELD)}
                                aria-labelledby={ID_FIRST_NAME_FIELD}
                                autoFocus={true}
                                disabled={isLoading}
                                placeholder={t('AccountSetupForm.firstNamePlaceholder')}
                                {...register('firstName', {
                                    required: t('Required.fieldGeneric', { ns: 'shared' }),
                                    maxLength: 255,
                                    minLength: 1
                                })}
                            />
                        </FormField>
                        <FormField
                            grow={true}
                            inputId={ID_LAST_NAME_FIELD}
                            label={t('AccountSetupForm.lastNameLabel')}
                            {...formFieldErrorPropsV2(
                                formState.errors.lastName,
                                undefined,
                                t('Required.fieldErrorLabel', { ns: 'shared' })
                            )}>
                            <TextInput
                                {...autoId(ID_LAST_NAME_FIELD)}
                                aria-labelledby={ID_LAST_NAME_FIELD}
                                disabled={isLoading}
                                placeholder={t('AccountSetupForm.lastNamePlaceholder')}
                                {...register('lastName', {
                                    required: t('Required.fieldGeneric', { ns: 'shared' }),
                                    maxLength: 255,
                                    minLength: 1
                                })}
                            />
                        </FormField>
                        <PasswordTextFieldWithConfirm
                            passwordUpdated={passwordUpdated}
                            setShowPasswordErrors={setShowPasswordErrors}
                            showPasswordErrors={showPasswordErrors}
                            isDisabled={isLoading}
                            shouldAutoFocus={false}
                        />
                    </div>
                    <PrimaryButton
                        {...autoId(ID_PASSWORD_BUTTON)}
                        marginTop={'1.5rem'}
                        maxWidth={'21rem'}
                        disabled={isLoading}
                        size='large'
                        type='submit'
                        grow={true}>
                        {t('AccountSetupForm.createAccount')}
                    </PrimaryButton>
                </form>

                <p className={styles.terms}>
                    <Trans i18nKey={'Shared.createAccountDisclaimer'}>
                        By clicking the
                        <strong>“Create Account”</strong>
                        button, you accept our
                        <TranslatedExternalLink translationKey='termsUrl'>Terms of Use</TranslatedExternalLink>
                        and acknowledge the
                        <TranslatedExternalLink translationKey='privacyUrl'>Privacy Statement</TranslatedExternalLink>.
                    </Trans>
                </p>
            </div>
        </>
    );
}
